"use client"

import { Box, Button, Table, Thead, Tbody, Tr, Th, Td, Text, Tooltip } from "@chakra-ui/react"
import { FaRoute } from "react-icons/fa"

import { Panel } from "../../../components/shared/Section"
import { TableSkeleton } from "../../../components/shared/LoadingBlock"
import { neutral, brand, signal, type, numeric } from "../../../theme/tokens"
import { fmtR, fmtPct, pctTone } from "../format"

const th = { fontSize: type.size.micro, letterSpacing: type.tracking.label, color: neutral[500], bg: neutral[25], borderColor: neutral[200], py: 2, position: "sticky", top: 0, zIndex: 1 }
const numTd = { textAlign: "right", sx: numeric, fontSize: type.size.data, py: 1.5, borderColor: neutral[100], whiteSpace: "nowrap" }

const Pct = ({ v }) => (
  <Text as="span" color={pctTone(v, signal, neutral)} fontWeight={v == null ? type.weight.regular : type.weight.semibold}>
    {fmtPct(v)}
  </Text>
)

/**
 * One line per rep — the total row of every FILE sheet side by side — plus the stores
 * with sales that no rep looks after ("Unassigned") and the grand total. Clicking a rep
 * selects it for the tracker below.
 *
 * `routing` (the combined Rep Sales & Routing page, 2026-10-12 Helena): the Rep Routing summary for its own date
 * range — each rep's line then carries Scheduled … Days and a Route button too, so all of a rep is on one line.
 * Matched on the rep's name (rep_stores and field_reps carry the same names). Routing-only reps (no stores
 * assigned for sales) get a line with blank sales.
 * Store-based (Helena 2026-10-12): Scheduled = the rep's stores (every store, every month); Missed = stores not
 * visited yet this month; Strike rate = stores visited ÷ stores; Road km = planned km of the days the rep went out,
 * with the month's planned km above it in red (the km budget); Days = days the rep went out (a visit ticked).
 */
const routeTd = { ...numTd, color: neutral[700] }
const strike = (stores, seen) => (stores ? Math.round((100 * seen) / stores) : 0)
const km = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-ZA"))

const RepSummary = ({ data, isLoading, selectedRep, onSelectRep, routing = null, onRoute }) => {
  if (isLoading || !data) return <Panel padded={false}><TableSkeleton rows={10} minH="300px" /></Panel>
  const L = data.period?.labels || {}
  const byRep = routing ? new Map((routing.perRep || []).map((r) => [r.rep_name, r])) : null
  const salesNames = new Set((data.reps || []).map((l) => l.rep))
  const routingOnly = routing ? (routing.perRep || []).filter((r) => !salesNames.has(r.rep_name)).map((r) => ({ rep: r.rep_name, routingOnly: true })) : []
  const lines = [...(data.reps || []), ...routingOnly, ...(data.unassigned ? [data.unassigned] : [])]
  // Two header rows (combined page): the whole head sticks, not each cell — two sticky rows at top 0 overlap.
  const hd = routing ? { ...th, position: undefined, top: undefined } : th
  const routeTotal = routing
    ? (routing.perRep || []).reduce((a, r) => {
        for (const k of ["visited", "late", "additional", "stores_visited", "days_out", "km_done", "km_est"]) a[k] += Number(r[k]) || 0
        return a
      }, { visited: 0, late: 0, additional: 0, stores_visited: 0, days_out: 0, km_done: 0, km_est: 0 })
    : null
  const storesTotal = (data.reps || []).reduce((a, l) => a + (Number(l.stores) || 0), 0)
  const routeCells = (r, { total = false, stores = 0 } = {}) => {
    if (!routing) return null
    if (!r) return Array.from({ length: 9 }, (_, i) => <Td key={`r${i}`} {...routeTd} borderLeft={i === 0 ? "1px solid" : undefined} borderLeftColor={neutral[200]} />)
    return [
      <Td key="sch" {...routeTd} borderLeft="1px solid" borderLeftColor={neutral[200]}>{stores}</Td>,
      <Td key="vis" {...routeTd}>{r.visited + r.late}</Td>,
      <Td key="late" {...routeTd}>{r.late}</Td>,
      <Td key="add" {...routeTd}>{r.additional}</Td>,
      <Td key="mis" {...routeTd} color={stores - r.stores_visited > 0 ? signal.down : routeTd.color}>{Math.max(0, stores - r.stores_visited)}</Td>,
      <Td key="str" {...routeTd} fontWeight={type.weight.semibold}>{strike(stores, r.stores_visited)}%</Td>,
      <Td key="km" {...routeTd} lineHeight="1.2">
        <Tooltip label="Estimated road km for the month's planned route (the km budget)" openDelay={300} fontSize="xs">
          <Text as="div" fontSize={type.size.micro} color={signal.down}>est {km(r.km_est)}</Text>
        </Tooltip>
        {km(r.km_done)}
      </Td>,
      <Td key="days" {...routeTd}>{r.days_out}</Td>,
      <Td key="go" py={1} borderColor={neutral[100]}>
        {!total && onRoute && (
          <Button size="xs" variant="ghost" leftIcon={<FaRoute />} onClick={(e) => { e.stopPropagation(); onRoute(r.rep_name) }}>Route</Button>
        )}
      </Td>,
    ]
  }

  const row = (l, { total = false } = {}) => {
    const active = !total && selectedRep === l.rep
    const clickable = !total && typeof onSelectRep === "function"
    return (
      <Tr
        key={l.rep}
        onClick={clickable ? () => onSelectRep(l.rep) : undefined}
        cursor={clickable ? "pointer" : "default"}
        bg={active ? brand[50] : total ? neutral[50] : "transparent"}
        _hover={clickable && !active ? { bg: neutral[100] } : {}}
        fontWeight={total ? type.weight.semibold : type.weight.regular}
        // The total stays in view while the rep list scrolls.
        position={total ? "sticky" : undefined}
        bottom={total ? 0 : undefined}
        zIndex={total ? 1 : undefined}
        boxShadow={total ? `0 -1px 0 ${neutral[200]}` : undefined}
      >
        <Td py={1.5} borderColor={neutral[100]} fontSize={type.size.data} whiteSpace="nowrap" color={active ? brand[600] : neutral[800]} fontWeight={active || total ? type.weight.semibold : type.weight.regular}>
          {l.rep === "Unassigned" ? (
            <Tooltip label={`Stores with sales that are not assigned to a rep${l.storeList ? `: ${l.storeList.join(", ")}` : ""}`} openDelay={300} fontSize="xs">
              <Text as="span" color={neutral[500]} fontStyle="italic">Unassigned</Text>
            </Tooltip>
          ) : (
            l.rep
          )}
        </Td>
        {l.routingOnly ? (
          <Td {...numTd} colSpan={7} textAlign="left" color={neutral[400]} fontStyle="italic">no stores assigned for sales</Td>
        ) : (
          <>
            <Td {...numTd} color={neutral[500]}>{l.storesWithSales} / {l.stores}</Td>
            <Td {...numTd}>{fmtR(l.cur_ly)}</Td>
            <Td {...numTd} fontWeight={type.weight.semibold}>{fmtR(l.cur_ty)}</Td>
            <Td {...numTd}><Pct v={l.cur_yoy} /></Td>
            <Td {...numTd}>{fmtR(l.prev_ly)}</Td>
            <Td {...numTd} fontWeight={type.weight.semibold}>{fmtR(l.prev_ty)}</Td>
            <Td {...numTd}><Pct v={l.prev_yoy} /></Td>
          </>
        )}
        {routing && (total
          ? routeCells(routeTotal, { total: true, stores: storesTotal })
          : l.rep === "Unassigned"
            ? routeCells(null)
            : routeCells(byRep.get(l.rep) || { rep_name: l.rep, visited: 0, late: 0, additional: 0, stores_visited: 0, days_out: 0, km_done: 0, km_est: 0 }, { stores: Number(l.stores) || 0 }))}
      </Tr>
    )
  }

  return (
    <Panel padded={false}>
      <Box maxH={routing ? "640px" : "520px"} overflowY="auto" overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead position={routing ? "sticky" : undefined} top={0} zIndex={2}>
            {routing && (
              <Tr>
                <Th {...hd} colSpan={8} textAlign="left">Sales at cost</Th>
                <Th {...hd} colSpan={9} textAlign="left" borderLeft="1px solid" borderLeftColor={neutral[200]}>Routing · {routing.from} to {routing.to} · visits to date</Th>
              </Tr>
            )}
            <Tr>
              <Th {...hd}>Rep</Th>
              <Th {...hd} textAlign="right">Stores</Th>
              <Th {...hd} textAlign="right">{L.curLY}</Th>
              <Th {...hd} textAlign="right">{L.cur} <Text as="span" color={neutral[400]} fontWeight={type.weight.regular}>MTD</Text></Th>
              <Th {...hd} textAlign="right">YoY %</Th>
              <Th {...hd} textAlign="right">{L.prevLY}</Th>
              <Th {...hd} textAlign="right">{L.prev}</Th>
              <Th {...hd} textAlign="right">YoY %</Th>
              {routing && (
                <>
                  <Th {...hd} textAlign="right" borderLeft="1px solid" borderLeftColor={neutral[200]}>Scheduled stores</Th>
                  <Th {...hd} textAlign="right">Visited on schedule</Th>
                  <Th {...hd} textAlign="right">Caught up / early</Th>
                  <Th {...hd} textAlign="right">Additional</Th>
                  <Th {...hd} textAlign="right">Missed stores</Th>
                  <Th {...hd} textAlign="right">Strike rate</Th>
                  <Th {...hd} textAlign="right">Road km <Text as="span" color={signal.down} fontWeight={type.weight.regular}>(est)</Text></Th>
                  <Th {...hd} textAlign="right">Days out</Th>
                  <Th {...hd}></Th>
                </>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {lines.map((l) => row(l))}
            {data.total && row(data.total, { total: true })}
          </Tbody>
        </Table>
      </Box>
    </Panel>
  )
}

export default RepSummary
